import api from '../config/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

/**
 * Retail Orders Service - API client for B2B/wholesale orders management
 */

// ============================================================================
// RETAIL ORDERS
// ============================================================================

export const getRetailOrders = async (brandId, params = {}) => {
  const response = await api.get(`${API_BASE}/api/cogs/brands/${brandId}/retail-orders`, { params });
  return response.data;
};

export const getRetailOrder = async (brandId, orderId) => {
  const response = await api.get(`${API_BASE}/api/cogs/brands/${brandId}/retail-orders/${orderId}`);
  return response.data;
};

export const createRetailOrder = async (brandId, data) => {
  const response = await api.post(`${API_BASE}/api/cogs/brands/${brandId}/retail-orders`, data);
  return response.data;
};

export const updateRetailOrder = async (brandId, orderId, data) => {
  const response = await api.put(`${API_BASE}/api/cogs/brands/${brandId}/retail-orders/${orderId}`, data);
  return response.data;
};

export const deleteRetailOrder = async (brandId, orderId) => {
  const response = await api.delete(`${API_BASE}/api/cogs/brands/${brandId}/retail-orders/${orderId}`);
  return response.data;
};

// Parse an uploaded order PDF (LLM extraction + SKU resolution) into a prefill
// payload for the order forms. Parse-only — does NOT create an order. axios v1
// fills in the multipart boundary when Content-Type is 'multipart/form-data'.
export const parseOrderPdf = async (file, { brandId, orderType = 'wholesale', tierId, warehouseCode } = {}) => {
  const form = new FormData();
  form.append('document', file);
  form.append('brand_id', brandId);
  form.append('order_type', orderType);
  if (tierId) form.append('tier_id', tierId);
  if (warehouseCode) form.append('warehouse_code', warehouseCode);
  const response = await api.post(`${API_BASE}/api/cogs/order-pdf/parse`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Open the stored source PDF for an order in a new tab. The authenticated
// endpoint checks tenancy and returns the document's public CDN URL
// (storage.googleapis.com — same serving model as the creative cache).
export const downloadOrderSourcePdf = async (brandId, orderId) => {
  const response = await api.get(
    `${API_BASE}/api/cogs/brands/${brandId}/retail-orders/${encodeURIComponent(orderId)}/source-pdf`
  );
  const url = response.data?.url;
  if (!url) throw new Error('No source PDF stored for this order');
  const win = window.open(url, '_blank', 'noopener');
  if (!win) {
    // Popup blocked (window.open after an awaited XHR) — fall back to an anchor click.
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

// ============================================================================
// WHOLESALE ORDERS (cross-brand Ready-to-Invoice queue + auto-priced create)
// ============================================================================

// Create a wholesale order — auto-fills tier/subsidiary/terms from CWE, auto-prices from price_tier_items.
export const createWholesaleOrder = async (brandId, data) => {
  const response = await api.post(`${API_BASE}/api/cogs/brands/${brandId}/retail-orders/wholesale`, data);
  return response.data;
};

// Update a PENDING wholesale order. Releases the old reservations, deletes the
// old row, and re-runs the create flow under the same order_id. Backend rejects
// shipped/invoiced orders with 409 — caller must show that to the user.
export const updateWholesaleOrder = async (brandId, orderId, data) => {
  const response = await api.put(`${API_BASE}/api/cogs/brands/${brandId}/retail-orders/${orderId}/wholesale`, data);
  return response.data;
};

// Soft-void a PENDING wholesale order. `reason` is required (audit trail).
// Shipped / partially_shipped orders must be unshipped first via unshipWholesaleOrder.
// opts.invoice_voided_externally: for a LEGACY CIN7 order whose invoice was voided
// in QuickBooks (Cin7 is retired, so the void never syncs back) — clears the stale
// qb_invoice_id server-side so the void can proceed.
export const voidWholesaleOrder = async (brandId, orderId, reason, opts = {}) => {
  const response = await api.post(
    `${API_BASE}/api/cogs/brands/${brandId}/retail-orders/${orderId}/void`,
    { reason, ...(opts.invoice_voided_externally ? { invoice_voided_externally: true } : {}) }
  );
  return response.data;
};

// Reverse a void — re-places inventory hold, returns order to pending.
// Operator's recovery path for "voided by mistake". `reason` required for
// audit trail. Returns 409 if stock no longer available or order already
// invoiced; surface those messages inline rather than as generic errors.
export const unvoidWholesaleOrder = async (brandId, orderId, reason) => {
  const response = await api.post(
    `${API_BASE}/api/cogs/brands/${brandId}/retail-orders/${orderId}/unvoid`,
    { reason }
  );
  return response.data;
};

// Ship a wholesale order — fulfills inventory, locks FX rate, writes order_fulfillments.
export const shipWholesaleOrder = async (brandId, orderId, body = {}) => {
  const response = await api.post(`${API_BASE}/api/cogs/brands/${brandId}/retail-orders/${orderId}/ship`, body);
  return response.data;
};

// Cancel the LATEST shipment on a wholesale order. HARD-DELETES the
// shipment-caused inventory_transactions rows (so the deduction is voided as
// if it never happened, not credited back), bumps the affected batches back
// in place, restores the reservation slice, flips fulfillment_status back to
// pending (or partially_shipped if older partials remain). The deleted-tx
// list is captured in audit_log. `reason` is required for the audit trail.
export const unshipWholesaleOrder = async (brandId, orderId, reason) => {
  const response = await api.post(
    `${API_BASE}/api/cogs/brands/${brandId}/retail-orders/${orderId}/unship`,
    { reason }
  );
  return response.data;
};

// Update internal-only annotations on a connector-imported order (thg_procure
// et al). Strict allowlist on the backend — only order_notes / tags /
// warehouse_code / tracking_number / carrier are accepted; pricing / line
// items / customer link are untouchable here.
export const updateConnectorOrderAnnotations = async (brandId, orderId, payload) => {
  const response = await api.put(
    `${API_BASE}/api/cogs/brands/${brandId}/retail-orders/${orderId}/annotations`,
    payload
  );
  return response.data;
};

// Upsert per-line internal-allocation rows for a connector-imported order.
// Sidecar to products_info (which is a THG/SPS sync mirror). The portal-side
// quantity_dispatched / quantity_receipted stay untouched; this is our local
// "how much we're allocating from our inventory" record per line.
// Payload: { lines: [{ line_key, allocated_quantity, allocated_warehouse_code?, allocated_notes?, local_sku? }, ...] }
export const updateConnectorLineAnnotations = async (brandId, orderId, lines) => {
  const response = await api.put(
    `${API_BASE}/api/cogs/brands/${brandId}/retail-orders/${orderId}/line-annotations`,
    { lines }
  );
  return response.data;
};

// Pending-ship list (cross-brand wholesale orders with fulfillment_status='pending')
export const listPendingShipOrders = async () => {
  const response = await api.get(`${API_BASE}/api/wholesale-orders`, { params: { pending_ship: true } });
  return response.data;
};

// Voided wholesale orders list (is_voided=1) — for the "Voided" filter tab.
export const listVoidedWholesaleOrders = async () => {
  const response = await api.get(`${API_BASE}/api/wholesale-orders`, { params: { voided_only: true } });
  return response.data;
};

// Shipped wholesale orders list — counterpart to listPendingShipOrders so the
// "Shipped" tab on the Pending Ship page can surface what already left.
export const listShippedWholesaleOrders = async () => {
  const response = await api.get(`${API_BASE}/api/wholesale-orders`, { params: { shipped_only: true } });
  return response.data;
};

// List wholesale orders across all brand DBs (default: Ready-to-Invoice queue).
export const listWholesaleOrders = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/wholesale-orders`, { params });
  return response.data;
};

// Summary grouped by subsidiary.
export const getWholesaleOrdersSummary = async () => {
  const response = await api.get(`${API_BASE}/api/wholesale-orders/summary`);
  return response.data;
};

// Mark a wholesale order as invoiced.
export const markWholesaleOrderInvoiced = async (brandId, orderId, qbInvoiceId, qbInvoiceDate) => {
  const response = await api.post(
    `${API_BASE}/api/wholesale-orders/${brandId}/${orderId}/mark-invoiced`,
    { qb_invoice_id: qbInvoiceId, qb_invoice_date: qbInvoiceDate }
  );
  return response.data;
};

// ============================================================================
// BANK RECONCILIATION (incoming bank credits → open invoices, matched by amount)
// ============================================================================

// Preview proposed reconciliations for a brand: incoming bank credits
// (Wise/Revolut) matched to open wholesale invoices by exact amount. Returns
// { success, count, proposals: [...], brand_db }. Read-only — proposes nothing
// is applied until applyBankReconciliation is called.
export const getBankReconciliationProposals = async (brandId) => {
  const response = await api.get(
    `${API_BASE}/api/wholesale-orders/admin/bank-reconciliation/preview`,
    { params: { brand_id: brandId } }
  );
  return response.data;
};

// Apply the confirmed reconciliations — marks the matched orders paid.
// `items` = [{ order_id, bank_transaction_id, bank_source, amount }, ...].
// Returns { success, applied, total, results }.
export const applyBankReconciliation = async (brandId, items) => {
  const response = await api.post(
    `${API_BASE}/api/wholesale-orders/admin/bank-reconciliation/apply`,
    { brand_id: brandId, items }
  );
  return response.data;
};

// Customer picker for the SO entry form (CWE joined with QB customer info).
export const listWholesaleCustomers = async () => {
  const response = await api.get(`${API_BASE}/api/wholesale-orders/customers`);
  return response.data;
};
export const createWholesaleCustomer = async (data) => {
  const response = await api.post(`${API_BASE}/api/wholesale-orders/customers`, data);
  return response.data;
};
export const updateWholesaleCustomer = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/wholesale-orders/customers/${id}`, data);
  return response.data;
};
// Delete a wholesale customer. Backend returns 409 with { code: 'customer_has_orders',
// count, sample_order_ids } if the customer has any non-voided retailer/distributor
// orders — caller MUST handle that case (offer "Mark inactive instead" fallback).
export const deleteWholesaleCustomer = async (id) => {
  const response = await api.delete(`${API_BASE}/api/wholesale-orders/customers/${id}`);
  return response.data;
};

// Bulk import wholesale customers — body { rows: [...] }
export const bulkImportWholesaleCustomers = async (rows) => {
  const response = await api.post(`${API_BASE}/api/wholesale-orders/customers/bulk-import`, { rows });
  return response.data;
};

// Bulk update wholesale customers — body { updates: [{ id, ...changes }, ...] }
export const bulkUpdateWholesaleCustomers = async (updates) => {
  const response = await api.post(`${API_BASE}/api/wholesale-orders/customers/bulk-update`, { updates });
  return response.data;
};

// CSV export — triggers a browser download. Round-trippable through Update from CSV.
export const exportWholesaleCustomersCsv = async () => {
  const response = await api.get(`${API_BASE}/api/wholesale-orders/customers/export.csv`, {
    responseType: 'blob',
  });
  let filename = `wholesale-customers-${new Date().toISOString().slice(0, 10)}.csv`;
  const cd = response.headers?.['content-disposition'];
  if (cd) {
    const m = cd.match(/filename="?([^"]+)"?/);
    if (m) filename = m[1];
  }
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  return { success: true, filename };
};

// Auto-priced SKU list for a given tier, optionally scoped to a single brand.
// Pass `brandId` so the wholesale form's SKU dropdown only shows products that
// belong to the currently-selected brand. Pass `warehouseCode` to also receive
// `available_quantity` per SKU at that warehouse — used for the under-input
// "Available: N" hint on each line item.
export const listTierSkus = async (tierId, brandId = null, warehouseCode = null, beforeDate = null) => {
  const params = {};
  if (brandId) params.brand_id = brandId;
  if (warehouseCode) params.warehouse_code = warehouseCode;
  // beforeDate enables inbound_quantity per row (open POs + in-transit transfers
  // expected by that date at warehouseCode). YYYY-MM-DD format.
  if (beforeDate) params.before_date = beforeDate;
  const response = await api.get(`${API_BASE}/api/wholesale-orders/tiers/${tierId}/skus`, { params });
  return response.data;
};

export const uploadRetailOrdersCsv = async (brandId, csvData) => {
  const response = await api.post(`${API_BASE}/api/cogs/brands/${brandId}/retail-orders/upload-csv`, {
    csv_data: csvData
  });
  return response.data;
};

// Helper function to parse CSV for retail orders
export const parseRetailOrdersCSV = (csvText) => {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) {
    throw new Error('CSV file is empty or has no data rows');
  }

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  const orders = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim());
    if (values.length !== headers.length) continue;

    const row = {};
    headers.forEach((header, index) => {
      row[header] = values[index];
    });

    orders.push(row);
  }

  return orders;
};

// Helper function to format order for display
export const formatOrderForDisplay = (order) => {
  return {
    ...order,
    event_date: order.event_date ? new Date(order.event_date).toLocaleDateString() : '-',
    total_amount: order.total_amount ? `£${parseFloat(order.total_amount).toFixed(2)}` : '-',
    payment_status: order.payment_status || 'pending',
    fulfillment_status: order.fulfillment_status || 'pending'
  };
};
